import { useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Search,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { tx, getLocale, formatDuration } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/domain/provider";
import { addDays, todayAt, validDate } from "@/domain/time";

export interface RouteProps {
  query: URLSearchParams;
  onQuery: (
    patch: Record<string, string | null>,
    options?: { replace?: boolean; skipGuard?: boolean },
  ) => void;
}
/** Selection is UI route state; changing views or using Back never claims operational records. */
export function useQuerySelection(
  query: URLSearchParams,
  onQuery: RouteProps["onQuery"],
): [string[], (value: string[] | ((previous: string[]) => string[])) => void] {
  let selected: string[] = [];
  try {
    const parsed: unknown = JSON.parse(query.get("selected") || "[]");
    if (Array.isArray(parsed))
      selected = [
        ...new Set(
          parsed.filter((item): item is string => typeof item === "string"),
        ),
      ];
  } catch {
    /* An invalid URL selection is empty. */
  }
  const current = useRef(selected);
  current.current = selected;
  return [
    selected,
    (value) => {
      const next = [
        ...new Set(
          typeof value === "function" ? value(current.current) : value,
        ),
      ];
      current.current = next;
      onQuery({ selected: next.length ? JSON.stringify(next) : null });
    },
  ];
}

export const uid = (prefix: string) => `${prefix}-${crypto.randomUUID()}`;
export const safeDate = (value: string | null, timeZone = "Europe/Helsinki") =>
  value && validDate(value) ? value : todayAt(timeZone);
export const fieldClass = "grid min-w-0 gap-1.5 text-sm font-medium";
export const panelClass = "rounded-xl border border-border bg-card";
export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className={fieldClass}>
      <span>{tx(label)}</span>
      {children}
      {hint && (
        <span className="text-xs font-normal leading-5 text-muted-foreground">
          {tx(hint)}
        </span>
      )}
    </label>
  );
}
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
      <div>
        {eyebrow && (
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {tx(eyebrow)}
          </p>
        )}
        <h1 className="text-2xl font-semibold tracking-tight">{tx(title)}</h1>
        {description && (
          <p className="mt-1.5 max-w-2xl text-sm leading-6 text-muted-foreground">
            {tx(description)}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}
export function EmptyState({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-2 p-8 text-center">
      <Search className="mb-1 size-6 text-muted-foreground" />
      <p className="font-medium">{tx(title)}</p>
      <p className="max-w-md text-sm leading-6 text-muted-foreground">
        {tx(description)}
      </p>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}
export function ErrorMessage({ error }: { error: string }) {
  return error ? (
    <p
      role="alert"
      className="rounded-lg border border-destructive/25 bg-destructive/5 p-3 text-sm leading-6 text-destructive"
    >
      {tx(error)}
    </p>
  ) : null;
}
export function DraftRecoveryActions({
  data,
  filename,
  onReload,
}: {
  data: unknown;
  filename: string;
  onReload?: () => void;
}) {
  const exportDraft = () => {
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={exportDraft}>
        <Download />
        {tx("ops.export.recovery.draft.f877nx")}
      </Button>
      {onReload && (
        <Button size="sm" variant="ghost" onClick={onReload}>
          <RefreshCw />
          {tx("ops.reload.current.record.1l7jcde")}
        </Button>
      )}
    </div>
  );
}

export function Notice({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      className="rounded-lg border border-border bg-muted/60 px-4 py-3 text-sm leading-6"
    >
      {children}
    </div>
  );
}
export function StatusBadge({
  value,
  tone,
}: {
  value: string;
  tone?: "warning" | "success" | "danger";
}) {
  return (
    <Badge
      variant={
        tone === "warning"
          ? "warning"
          : tone === "success"
            ? "success"
            : tone === "danger"
              ? "danger"
              : "secondary"
      }
    >
      {tx(value)}
    </Badge>
  );
}
export const VIEW_MESSAGE_IDS = {
  list: "ops.list.1360n8h",
  calendar: "ops.calendar.17xfl1b",
  agenda: "ops.agenda.1xbkjzp",
  timeline: "ops.timeline.djv8ja",
} as const;

export function ViewSwitch({
  value,
  onChange,
  options = ["list", "calendar"],
}: {
  value: string;
  onChange: (value: string) => void;
  options?: Array<keyof typeof VIEW_MESSAGE_IDS>;
}) {
  return (
    <Tabs value={value} onValueChange={onChange}>
      <TabsList aria-label={tx("ops.view.q5w460")} className="h-10">
        {options.map((option) => (
          <TabsTrigger key={option} value={option} className="capitalize">
            {tx(VIEW_MESSAGE_IDS[option])}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
export function PeriodControl({
  date,
  onChange,
  days = 7,
  timeZone = "Europe/Helsinki",
}: {
  date: string;
  onChange: (date: string) => void;
  days?: number;
  timeZone?: string;
}) {
  const {clock} = useWorkspace();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex rounded-md border border-border">
        <Button
          variant="ghost"
          size="icon"
          aria-label={tx("ops.previous.period.1eee7nd")}
          onClick={() => onChange(addDays(date, -days))}
        >
          <ChevronLeft />
        </Button>
        <Button variant="ghost" onClick={() => onChange(clock.today(timeZone))}>
          {tx("ops.today.1sawk0u")}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={tx("ops.next.period.das515")}
          onClick={() => onChange(addDays(date, days))}
        >
          <ChevronRight />
        </Button>
      </div>
      <Input
        className="w-40"
        type="date"
        aria-label={tx("ops.period.start.pt0kw0")}
        value={date}
        onInput={(event) =>
          event.currentTarget.value && onChange(event.currentTarget.value)
        }
        onChange={(event) => event.target.value && onChange(event.target.value)}
      />
      <span className="text-sm text-muted-foreground">
        {formatDate(date)}
        {days > 1 && <> – {formatDate(addDays(date, days - 1))}</>}
      </span>
    </div>
  );
}
/** An unbounded query should show its actual extent instead of an inactive weekly selector. */
export function FullRangeControl({
  dates,
  onToday,
  timeZone = "Europe/Helsinki",
}: {
  dates: Array<string | null | undefined>;
  onToday: (date: string) => void;
  timeZone?: string;
}) {
  const {clock} = useWorkspace();
  const dated = [
    ...new Set(dates.filter((date): date is string => Boolean(date))),
  ].sort();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Badge variant="secondary">{tx("ops.all.dates.ry0hlt")}</Badge>
      <span className="text-sm text-muted-foreground">
        {dated.length ? (
          <>
            {formatDate(dated[0])}
            {dated.length > 1 && <> – {formatDate(dated[dated.length - 1])}</>}
          </>
        ) : (
          "—"
        )}
        {dates.some((date) => !date) && (
          <> · {tx("ops.undated.backlog.10t83k5")}</>
        )}
      </span>
      <Button variant="outline" onClick={() => onToday(clock.today(timeZone))}>
        {tx("ops.today.1sawk0u")}
      </Button>
    </div>
  );
}

export function Drawer({
  title,
  description,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        unpadded
        showCloseButton={false}
        className={cn(
          "inset-y-0 start-auto end-0 top-0 h-dvh max-h-dvh w-full max-w-2xl translate-x-0 translate-y-0 rounded-none",
          wide && "max-w-4xl",
        )}
      >
        <div className="flex h-dvh flex-col">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5 sm:px-7">
            <div>
              <DialogTitle className="text-xl">{tx(title)}</DialogTitle>
              {description && (
                <DialogDescription className="mt-1">
                  {tx(description)}
                </DialogDescription>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={onClose}
              aria-label={tx("ops.close.details.1soutj5")}
            >
              <X />
            </Button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-7">
            {children}
          </div>
          {footer && (
            <footer className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-border bg-card p-4 sm:px-7">
              {footer}
            </footer>
          )}
          <div data-guide-coach-host className="shrink-0" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
export function SkillsInput({
  value,
  onChange,
  suggestions = [],
  label = "Skills",
}: {
  value: string[];
  onChange: (value: string[]) => void;
  suggestions?: string[];
  label?: string;
}) {
  const [input, setInput] = useState("");
  const add = (skill: string) => {
    const trimmed = skill.trim();
    if (
      trimmed &&
      !value.some((item) => item.toLowerCase() === trimmed.toLowerCase())
    )
      onChange([...value, trimmed]);
    setInput("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {value.map((skill) => (
          <Badge key={skill} variant="outline" className="gap-1 pe-1">
            {skill}
            <button
              type="button"
              aria-label={`${tx("ops.remove.skill.3brh64")}: ${skill}`}
              className="grid size-7 place-items-center rounded hover:bg-muted"
              onClick={() => onChange(value.filter((item) => item !== skill))}
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          aria-label={tx(label)}
          value={input}
          placeholder={tx("ops.type.a.skill.and.press.enter.icua27")}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add(input);
            }
          }}
        />
        <Button
          variant="outline"
          onClick={() => add(input)}
          aria-label={tx("ops.add.skill.ih3s8h")}
        >
          <Check />
        </Button>
      </div>
      {input && (
        <div className="flex flex-wrap gap-1">
          {suggestions
            .filter(
              (skill) =>
                skill.toLowerCase().includes(input.toLowerCase()) &&
                !value.includes(skill),
            )
            .slice(0, 8)
            .map((skill) => (
              <Button
                key={skill}
                size="sm"
                variant="ghost"
                onClick={() => add(skill)}
              >
                {skill}
              </Button>
            ))}
        </div>
      )}
    </div>
  );
}
export function formatDate(
  value: string,
  options?: Intl.DateTimeFormatOptions,
) {
  return new Date(
    value.length === 10 ? `${value}T12:00:00Z` : value,
  ).toLocaleDateString(
    getLocale(),
    options ?? { day: "numeric", month: "short" },
  );
}
export function formatInstant(value?: string, timeZone = "Europe/Helsinki") {
  return value
    ? new Date(value).toLocaleString(getLocale(), {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        timeZone,
      })
    : "—";
}
export function formatMinutes(value: number) {
  return formatDuration(value);
}
export function PropertyFilter({
  value,
  onChange,
  properties,
}: {
  value: string;
  onChange: (value: string) => void;
  properties: { id: string; name: string }[];
}) {
  return (
    <NativeSelect
      className="h-10 min-w-44"
      aria-label={tx("ops.status.property")}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">{tx("ops.all.properties.6vtu5t")}</option>
      {properties.map((property) => (
        <option key={property.id} value={property.id}>
          {property.name}
        </option>
      ))}
    </NativeSelect>
  );
}
export function BackButton({
  onClick,
  label = "Back",
}: {
  onClick: () => void;
  label?: string;
}) {
  return (
    <Button variant="ghost" className="-ms-3" onClick={onClick}>
      <ArrowLeft />
      {tx(label)}
    </Button>
  );
}
export function NextButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button onClick={onClick}>
      {children}
      <ArrowRight />
    </Button>
  );
}
